import type { BaseState, CommonGraph, Metadata } from '@common/types';
import { CommonActionTypes } from '@common/types';
import { useReadOnlyFlag } from '@common/useReadOnlyFlag';
import { Callable } from '@prophecy/interfaces/generic';
import { ErrorTrace, message } from '@prophecy/ui';
import { usePersistentCallback, useThrottle } from '@prophecy/utils/react/hooks';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { Dispatch } from 'redux';

import { ReplayViewMode } from '../common/Replay/types';
import { useHistory } from '../HistoryManager/context';
import { CompilationStatus, WorkflowState } from '../redux/types';
import { isShareMode } from '../utils/shareToken';
import { toggleLoader } from './base/methods';
import { LSP } from './base/types';
import { LSPWebSocketClient } from './websocket/client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OverrideLSPMethod = createContext<any>(undefined);

export type LspMethodsOptions<TData = unknown> = {
  readonly?: boolean;
  onError?: (error: LSP.Error) => void;
  /** Called on success; `data` is the resolved result and is always defined. */
  onSuccess?: (data: TData) => void;
  showGlobalLoader?: boolean;
  skipStateUpdate?: boolean;
};

function onError(err: LSP.Error) {
  message.error({
    content: err.message,
    detail: err.data ? <ErrorTrace>{err.data}</ErrorTrace> : null
  });
}

function onSuccess(resp: unknown) {
  const content = resp && typeof resp === 'object' && 'message' in resp ? (resp as LSP.Success).message : undefined;
  if (content) {
    message.success({
      content
    });
  }
}

const defaultOptions = { onError, showGlobalLoader: false, onSuccess };

export function getIsShareOrReplayNonEditMode(): boolean {
  const searchParams = new URLSearchParams(window.location.search);
  const replayId = searchParams.get('replayId');
  const replayMode = searchParams.get('replayMode');
  return Boolean(isShareMode() || (replayId && replayMode !== ReplayViewMode.edit));
}
/**
 * When the IDE is in readonly mode, we don't want to send any LSP requests except for editorLoadView.
 * For historical mode, we don't want to send any LSP requests.
 * For dataset info, we don't want to send any LSP requests.
 * For share or replay mode,
 *   allow didAction for share or if replayMode is not edit,
 * @param params
 * @returns
 */
export function getIsLSPMethodDisabled(params: {
  readOnly: boolean;
  historical: boolean;
  isDatasetInfo: boolean;
  methodType: string;
}): boolean {
  const { readOnly, historical, isDatasetInfo, methodType } = params;
  if (!readOnly) return false;
  if (!historical && methodType === LSP.Method.editorLoadView) return false;
  if (isDatasetInfo) return false;
  if (getIsShareOrReplayNonEditMode()) return false;
  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExtractLSPCallback<M extends object, T extends keyof M> = M[T] extends (...arg: any) => infer R ? R : never;

export function createLSPMethodHook<
  C extends LSPWebSocketClient<string, string>,
  M extends object,
  MD extends Partial<{ [K in keyof M]: LspMethodsOptions }>
>(methods: M, methodDefaults: MD, getClient: () => C) {
  return function useLSPMethod<T extends keyof M>(
    type: T,
    options?: LspMethodsOptions<LSP.ExtractResolvedReturnType<ExtractLSPCallback<M, T>>>
  ) {
    type ResolvedData = LSP.ExtractResolvedReturnType<ExtractLSPCallback<M, T>>;
    type MethodReturn = LSP.MethodReturnType<ResolvedData, T extends LSP.Method ? T : LSP.Method>;

    const historyContext = useHistory();
    const _options = { ...defaultOptions, ...methodDefaults[type], ...options };
    const store = useStore<BaseState<CommonGraph, Metadata>>();

    const lspClient = getClient();

    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState<LSP.Error>();
    const [data, setData] = useState<ResolvedData | undefined>();
    const readOnly = useReadOnlyFlag();

    const showLoading = usePersistentCallback(() => {
      setLoading(true);
      if (_options.showGlobalLoader) toggleLoader(store)(true);
    });

    const hideLoading = usePersistentCallback(() => {
      setLoading(false);
      if (_options.showGlobalLoader) toggleLoader(store)(false);
    });

    const method = usePersistentCallback(async function dispatch(...args: LSP.ExtractParams<ExtractLSPCallback<M, T>>) {
      const [payload, ...rest] = args;

      const { historical, expectation } = store.getState();
      const _readOnly = options?.readonly ?? readOnly;
      const isDatasetInfo = Boolean(expectation?.isDatasetInfo);

      const isLSPMethodDisabled = getIsLSPMethodDisabled({
        readOnly: _readOnly,
        historical: Boolean(historical),
        isDatasetInfo,
        methodType: type as string
      });

      if (isLSPMethodDisabled) {
        return { error: undefined, data: undefined, isLoading: false, isError: false, type } as MethodReturn;
      }

      if (isDatasetInfo) {
        if (!_options.skipStateUpdate) store.dispatch({ type: type.toString(), payload });
        return { error, data, isLoading, isError: !!error, type };
      }
      let closeSubscription: Callable | undefined;
      try {
        showLoading();

        const _method = methods[type] as unknown as Callable;
        closeSubscription = lspClient?.listen(LSP.Notification.close, () => {
          hideLoading();
        });

        const lspPromise = _method(lspClient, store, historyContext)(payload, ...rest);
        /**
         * store on dispatch after calling lsp method. Store dispatch can synchronously trigger another didChange,
         * which can break the order od didChange happening on UI vs didChange going on backend.
         */
        if (!_options.skipStateUpdate) {
          store.dispatch({ type: type.toString(), payload });
        }
        const result = (await lspPromise) as ResolvedData;
        setData(result);
        setError(undefined);
        _options.onSuccess?.(result);
        return { data: result } as MethodReturn;
      } catch (err) {
        const _err = err as LSP.Error;
        setData(undefined);
        setError(_err);
        _options.onError?.(_err);
        throw _err;
      } finally {
        setLoading(false);
        if (_options.showGlobalLoader) toggleLoader(store)(false);
        closeSubscription?.();
      }
    }) as (...args: LSP.ExtractParams<ExtractLSPCallback<M, T>>) => Promise<MethodReturn>;
    const returnValue: {
      method: (...args: LSP.ExtractParams<ExtractLSPCallback<M, T>>) => Promise<MethodReturn>;
      error: LSP.Error | undefined;
      data: ResolvedData | undefined;
      isLoading: boolean;
      isError: boolean;
      type: T;
    } = { method, error, data, isLoading, isError: !!error, type };
    type R = typeof returnValue;
    const parentHook = useContext<R>(OverrideLSPMethod);
    // temp solution, currently it allows to override only 1 type, change it later to allow override for multiple types
    if (parentHook && parentHook.type === type) {
      // locking type with context
      return parentHook;
    }
    return returnValue;
  };
}

export function useComponentCompilationStatus(processId: string) {
  return useSelector<WorkflowState, CompilationStatus>((state) => state.compilationStatus[processId]);
}

export function useCreateLSPClient<T extends Callable>(
  createLSPClient: T
): {
  lspClient: ReturnType<T>;
} {
  // NOTE: keeping it as separate memo as useState callback gets called again in case the component is hot refreshed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialLspClient = useMemo(() => createLSPClient(), []);

  const [lspClient, setLspClient] = useState(initialLspClient);

  const reconnectLSPClient = usePersistentCallback(() => {
    setLspClient(createLSPClient());
  });
  useMemo(() => {
    // keep the reconnect method with the lspClient
    lspClient.reconnect = reconnectLSPClient;
  }, [lspClient, reconnectLSPClient]);

  return { lspClient };
}

export function useHandleLogMessageNotification(dispatch: Dispatch) {
  const throttledDispatch = useThrottle(
    ({ params }: { params: { message: string } }) => {
      dispatch({
        type: CommonActionTypes.logMessage,
        payload: params.message
      });
    },
    1000,
    { trailing: true, leading: true }
  );

  return useCallback(
    ({ params }: { params: { message: string } }) => {
      // at the end of log message don't wait just dispatch the message
      if (params.message === '') {
        throttledDispatch.cancel();
        dispatch({
          type: CommonActionTypes.logMessage,
          payload: params.message
        });
      } else {
        throttledDispatch({ params });
      }
    },
    [dispatch, throttledDispatch]
  );
}
